import React from 'react';
import Button from './common/Button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    // Clear cache and reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    localStorage.clear();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-dvh-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-red-50 to-rose-50 dark:from-slate-900 dark:via-slate-900 dark:to-red-950">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 shadow-2xl rounded-2xl sm:rounded-3xl border border-red-100 dark:border-red-500/20 p-6 sm:p-8 slide-in">
            <div className="text-center">
              <div className="inline-block mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-red-500/25">
                  <i className="fas fa-exclamation-triangle text-2xl text-white" aria-hidden="true"></i>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                Oops! Something went wrong
              </h2>

              <p className="text-slate-500 dark:text-slate-400 mb-6">
                We're sorry for the inconvenience. The application encountered an error.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mb-6 text-left">
                  <details className="bg-red-50 dark:bg-red-500/10 p-4 rounded-xl border border-red-200 dark:border-red-500/30">
                    <summary className="cursor-pointer font-semibold text-red-700 dark:text-red-400 mb-2">
                      Error Details (Development Mode)
                    </summary>
                    <div className="text-xs text-red-800 dark:text-red-300 overflow-auto max-h-48">
                      <p className="font-mono mb-2">{this.state.error.toString()}</p>
                      {this.state.errorInfo && (
                        <pre className="whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                </div>
              )}

              <div className="space-y-3">
                <Button variant="danger" fullWidth icon="fa-sync-alt" onClick={this.handleReload}>
                  Clear Cache & Reload
                </Button>

                <Button variant="secondary" fullWidth onClick={() => window.location.href = '/login'}>
                  Go to Login
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700">
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  If the problem persists, please contact support.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
